import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch } from "../../lib/api.js";
import { useToast } from "../Toast.js";

// ADR-0106/0107 — Model Train inspector. Authors the training config (family /
// algorithm / features + per-family params), creates the bound Model on first
// Train, kicks off training, and polls status. Supervised families take a target
// column + holdout; unsupervised families (anomaly, clustering) take no target —
// anomaly takes a contamination rate, clustering a fixed k or an auto-k range.
// Mirrors the persist inspector's syncFromSpec + setNodeData-autosave +
// flushAndSave-before-action patterns. Pickers come from upstream-columns.

type Family = "regression" | "classification" | "anomaly" | "clustering";

const UNSUPERVISED: ReadonlySet<Family> = new Set<Family>(["anomaly", "clustering"]);

const FAMILY_ALGOS: Record<Family, Array<{ id: string; label: string }>> = {
  regression: [
    { id: "linear_regression", label: "Linear Regression (Ridge)" },
    { id: "gradient_boosted_regressor", label: "Gradient Boosted (XGBoost)" },
  ],
  classification: [
    { id: "logistic_regression", label: "Logistic Regression" },
    { id: "gradient_boosted_classifier", label: "Gradient Boosted (XGBoost)" },
  ],
  anomaly: [{ id: "isolation_forest", label: "Isolation Forest" }],
  clustering: [{ id: "kmeans", label: "K-Means" }],
};

// ADR-0118 — missing-value handling (numeric only).
type MissingStrategy = "auto" | "impute" | "drop" | "error";
type NumericFill = "median" | "mean" | "constant";

type TrainConfig = {
  algorithm_family: Family;
  algorithm: string;
  target_column: string;
  feature_columns: string[];
  validation: { strategy: string; holdout_fraction: number };
  hyperparameters: Record<string, unknown>;
  hpo: { enabled: boolean };
  missing_values: { strategy: MissingStrategy; numeric: NumericFill };
  contamination?: number;
  n_clusters?: number;
  k_range?: [number, number];
};

type DriftChange = { field: string; label: string; before: string; after: string };
type SpecDrift = { has_drift: boolean; changes?: DriftChange[]; reason?: string };
type TrainingStage = "resolving_data" | "training" | "finalizing";
type StageTiming = { stage: TrainingStage; started_at: string; duration_ms: number | null };
type TrainingRow = {
  id: string;
  status: "queued" | "running" | "success" | "failed" | "cancelled";
  triggered_by_name?: string;
  error_message: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at: string | null;
  duration_ms?: number | null;
  row_count?: number | null;
  metrics: Record<string, unknown> | null;
  /** Live stage while running (in-memory on the hub); null otherwise. */
  stage?: TrainingStage | null;
};

// One upstream node run in a training's data-resolution chain (from execution_log).
type NodeRun = {
  node_id: string | null;
  label: string;
  kind: string;
  status: "success" | "failed";
  duration_ms: number | null;
  row_count: number | null;
  from_cache: boolean;
  started_at: string;
  error: string | null;
};
type TuningSummary = { ran?: boolean; reason?: string; searched_rows?: number; n_candidates?: number; cv_folds?: number; scoring?: string; cv_score?: number };

// The training pipeline as a uniform stage list — the hub's three separately-timed
// phases. Plain-language labels, no internal/runner jargon.
const STAGE_ORDER: { key: TrainingStage; label: string }[] = [
  { key: "resolving_data", label: "Prepare data" },
  { key: "training", label: "Train model" },
  { key: "finalizing", label: "Save model" },
];
const STAGE_INDEX: Record<TrainingStage, number> = { resolving_data: 0, training: 1, finalizing: 2 };

type StepState = "done" | "active" | "pending" | "failed" | "cancelled";

/**
 * Derive each stage's state from the training's status + live stage. Running:
 * stages before the live stage are done, the live one active, the rest pending.
 * Failed: stages with completion evidence are done; the first un-evidenced stage is
 * the failure point (no stage column, so we infer — a failed node run pins it to
 * Prepare data, otherwise it's the model build).
 */
function buildTrainingSteps(t: TrainingRow, nodeRuns: NodeRun[]): { key: TrainingStage; label: string; state: StepState }[] {
  const order = STAGE_ORDER;
  const prepFailed = nodeRuns.some((r) => r.status === "failed");
  const prepDone = nodeRuns.length > 0 && !prepFailed;

  if (t.status === "success") return order.map((s) => ({ ...s, state: "done" as StepState }));
  if (t.status === "queued") return order.map((s) => ({ ...s, state: "pending" as StepState }));
  if (t.status === "running") {
    const ai = t.stage ? STAGE_INDEX[t.stage] : -1;
    return order.map((s) => {
      const si = STAGE_INDEX[s.key];
      return { ...s, state: (si < ai ? "done" : si === ai ? "active" : "pending") as StepState };
    });
  }
  // failed / cancelled share the same evidence-based "where did it stop" inference;
  // only the badge on the stop stage differs (a cancel is not a failure).
  const stopState: StepState = t.status === "cancelled" ? "cancelled" : "failed";
  let stoppedAt = order.findIndex((s) => s.key !== "resolving_data" || !prepDone);
  if (stoppedAt === -1) stoppedAt = order.length - 1;
  return order.map((s, i) => ({ ...s, state: (i < stoppedAt ? "done" : i === stoppedAt ? stopState : "pending") as StepState }));
}

function fmtDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return "";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}
type VersionRow = {
  id: string;
  version_number: number;
  metrics: Record<string, unknown>;
  is_current: boolean;
  trained_at: string;
};
type ModelDetail = {
  model: { id: string; name: string; algorithm_family: Family };
  versions: VersionRow[];
  trainings: TrainingRow[];
  spec_drift: SpecDrift;
};

const ACTIVE_STATUSES = new Set(["queued", "running"]);

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function metricSummary(family: Family, metrics: Record<string, unknown> | null | undefined): string {
  if (!metrics) return "";
  const n = (k: string) => (typeof metrics[k] === "number" ? (metrics[k] as number) : null);
  const parts: string[] = [];
  if (family === "classification") {
    const auc = n("auc"); const acc = n("accuracy");
    if (auc !== null) parts.push(`AUC ${auc.toFixed(3)}`);
    if (acc !== null) parts.push(`Acc ${acc.toFixed(3)}`);
  } else if (family === "regression") {
    const rmse = n("rmse"); const r2 = n("r2");
    if (rmse !== null) parts.push(`RMSE ${rmse.toFixed(3)}`);
    if (r2 !== null) parts.push(`R² ${r2.toFixed(3)}`);
  } else if (family === "anomaly") {
    const fr = n("flagged_rate");
    if (fr !== null) parts.push(`Flagged ${(fr * 100).toFixed(1)}%`);
  } else if (family === "clustering") {
    const k = n("n_clusters"); const sil = n("silhouette");
    if (k !== null) parts.push(`k=${k}`);
    if (sil !== null) parts.push(`Silhouette ${sil.toFixed(3)}`);
  }
  // Present only when hyperparameter tuning ran — the best cross-validated score.
  const cv = n("cv_score");
  if (cv !== null) parts.push(`CV ${cv.toFixed(3)}`);
  return parts.join(" · ");
}

function readTrainSpec(data: OperationCanvasNodeData): { modelId: string | null; config: TrainConfig } {
  const sp = (data.config?.spec_model_train as Record<string, unknown> | undefined) ?? {};
  const c = (sp.config as Record<string, unknown> | undefined) ?? {};
  // Resolve the full family — a binary coalesce would silently map a saved
  // anomaly/clustering model to "classification" on load.
  const famRaw = c.algorithm_family;
  const family: Family =
    famRaw === "regression" || famRaw === "anomaly" || famRaw === "clustering" ? famRaw : "classification";
  const v = (c.validation as Record<string, unknown> | undefined) ?? {};
  const kr = Array.isArray(c.k_range) && c.k_range.length === 2
    ? ([Number((c.k_range as unknown[])[0]), Number((c.k_range as unknown[])[1])] as [number, number])
    : undefined;
  const mv = (c.missing_values as Record<string, unknown> | undefined) ?? {};
  const mvStrategy: MissingStrategy =
    mv.strategy === "impute" || mv.strategy === "drop" || mv.strategy === "error" ? mv.strategy : "auto";
  const mvNumeric: NumericFill =
    mv.numeric === "mean" || mv.numeric === "constant" ? mv.numeric : "median";
  return {
    modelId: typeof sp.model_id === "string" && sp.model_id ? sp.model_id : null,
    config: {
      algorithm_family: family,
      algorithm: typeof c.algorithm === "string" ? c.algorithm : "",
      target_column: typeof c.target_column === "string" ? c.target_column : "",
      feature_columns: Array.isArray(c.feature_columns)
        ? (c.feature_columns as unknown[]).filter((x): x is string => typeof x === "string")
        : [],
      validation: {
        strategy: typeof v.strategy === "string" ? v.strategy : "holdout",
        holdout_fraction: typeof v.holdout_fraction === "number" ? v.holdout_fraction : 0.2,
      },
      hyperparameters: (c.hyperparameters as Record<string, unknown>) ?? {},
      hpo: { enabled: Boolean((c.hpo as Record<string, unknown> | undefined)?.enabled) },
      missing_values: { strategy: mvStrategy, numeric: mvNumeric },
      contamination: typeof c.contamination === "number" ? c.contamination : undefined,
      n_clusters: typeof c.n_clusters === "number" ? c.n_clusters : undefined,
      k_range: kr,
    },
  };
}

/** Default model name follows the PDO convention: "{operation} / {node}". */
function defaultModelName(operationName: string, nodeLabel: string): string {
  const parts = [operationName, nodeLabel].map((s) => (s ?? "").trim()).filter(Boolean);
  return parts.join(" / ") || "Model";
}

export function OperationInspectorModelTrain({
  selectedNode,
  operationId,
  operationName,
  setNodeData,
  flushAndSave,
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  operationName: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  flushAndSave: () => Promise<boolean>;
}) {
  const toast = useToast();

  const [family, setFamily] = useState<Family>("classification");
  const [algorithm, setAlgorithm] = useState<string>("gradient_boosted_classifier");
  const [target, setTarget] = useState<string>("");
  const [features, setFeatures] = useState<string[]>([]);
  const [holdout, setHoldout] = useState<number>(0.2);
  const [hpoEnabled, setHpoEnabled] = useState<boolean>(false);
  const [missingStrategy, setMissingStrategy] = useState<MissingStrategy>("auto");
  const [missingNumeric, setMissingNumeric] = useState<NumericFill>("median");
  const [contamination, setContamination] = useState<number>(0.05);
  const [kMode, setKMode] = useState<"fixed" | "auto">("fixed");
  const [nClusters, setNClusters] = useState<number>(5);
  const [kRange, setKRange] = useState<[number, number]>([2, 8]);
  const [modelId, setModelId] = useState<string | null>(null);

  // ADR-0116 — architect-created nodes carry provisional intent (target_intent/
  // feature_intent) until reviewed. On open we refine intent → concrete columns.
  const [isProvisional, setIsProvisional] = useState(false);
  const [refine, setRefine] = useState<{ status: "idle" | "loading" | "resolved" | "unresolved"; unmatched: string[] }>({ status: "idle", unmatched: [] });
  const [refineDismissed, setRefineDismissed] = useState(false);

  const [upstreamColumns, setUpstreamColumns] = useState<string[]>([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsError, setColumnsError] = useState<string | null>(null);

  const [detail, setDetail] = useState<ModelDetail | null>(null);
  const [nodeRuns, setNodeRuns] = useState<NodeRun[]>([]);
  const [stageTimeline, setStageTimeline] = useState<StageTiming[]>([]);
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const specJson = JSON.stringify(selectedNode.data.config?.spec_model_train ?? null);

  // Sync form state from spec on node-id / spec change.
  useEffect(() => {
    const { modelId: mid, config } = readTrainSpec(selectedNode.data);
    setModelId(mid);
    setFamily(config.algorithm_family);
    setAlgorithm(config.algorithm || FAMILY_ALGOS[config.algorithm_family][0]!.id);
    setTarget(config.target_column);
    setFeatures(config.feature_columns);
    setHoldout(config.validation.holdout_fraction);
    setHpoEnabled(config.hpo.enabled);
    setMissingStrategy(config.missing_values.strategy);
    setMissingNumeric(config.missing_values.numeric);
    if (typeof config.contamination === "number") setContamination(config.contamination);
    if (config.k_range) { setKMode("auto"); setKRange(config.k_range); }
    else if (typeof config.n_clusters === "number") { setKMode("fixed"); setNClusters(config.n_clusters); }
    const rawCfg = ((selectedNode.data.config?.spec_model_train as Record<string, unknown> | undefined)?.config as Record<string, unknown> | undefined) ?? {};
    setIsProvisional(rawCfg.provisional === true);
    setRefineDismissed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id, specJson]);

  // ADR-0116 §5 Phase B — lazily refine provisional intent → concrete columns when
  // the inspector opens and the upstream schema resolves. Pre-fills the bound-column
  // pickers (state only — the spec stays provisional until Confirm or an edit).
  useEffect(() => {
    if (!isProvisional) { setRefine({ status: "idle", unmatched: [] }); return; }
    let cancelled = false;
    setRefine({ status: "loading", unmatched: [] });
    void (async () => {
      await flushAndSave(); // ensure the provisional config is persisted before the server reads it
      try {
        const r = await apiFetch<
          | { resolved: false }
          | { resolved: true; target_column: string | null; feature_columns: string[]; unmatched: string[]; candidates: string[] }
        >(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/refine-model-config`,
          { method: "POST", body: {} },
        );
        if (cancelled) return;
        if (!r.resolved) { setRefine({ status: "unresolved", unmatched: [] }); return; }
        if (r.target_column) setTarget(r.target_column);
        if (r.feature_columns.length) setFeatures(r.feature_columns);
        setRefine({ status: "resolved", unmatched: r.unmatched });
      } catch {
        if (!cancelled) setRefine({ status: "unresolved", unmatched: [] });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode.id, isProvisional]);

  const writeConfig = useCallback((next: TrainConfig) => {
    setNodeData(selectedNode.id, (d) => {
      const cfg = (d.config ?? {}) as Record<string, unknown>;
      const sp = ((cfg.spec_model_train as Record<string, unknown>) ?? {}) as Record<string, unknown>;
      return { ...d, config: { ...cfg, spec_model_train: { ...sp, config: next } } };
    });
  }, [selectedNode.id, setNodeData]);

  const writeModelId = useCallback((id: string) => {
    setNodeData(selectedNode.id, (d) => {
      const cfg = (d.config ?? {}) as Record<string, unknown>;
      const sp = ((cfg.spec_model_train as Record<string, unknown>) ?? {}) as Record<string, unknown>;
      return { ...d, config: { ...cfg, spec_model_train: { ...sp, model_id: id } } };
    });
  }, [selectedNode.id, setNodeData]);

  const buildConfig = useCallback((over: Partial<{ family: Family; algorithm: string; target: string; features: string[]; holdout: number; hpo: boolean; missingStrategy: MissingStrategy; missingNumeric: NumericFill; contamination: number; kMode: "fixed" | "auto"; nClusters: number; kRange: [number, number] }>): TrainConfig => {
    const fam = over.family ?? family;
    const unsupervised = UNSUPERVISED.has(fam);
    const cfg: TrainConfig = {
      algorithm_family: fam,
      algorithm: over.algorithm ?? algorithm,
      target_column: unsupervised ? "" : (over.target ?? target),
      feature_columns: over.features ?? features,
      validation: { strategy: "holdout", holdout_fraction: over.holdout ?? holdout },
      hyperparameters: {},
      // Tuning only applies to the supervised families; the unsupervised ones have
      // their own selection knobs (contamination / cluster count or range).
      hpo: { enabled: unsupervised ? false : (over.hpo ?? hpoEnabled) },
      missing_values: { strategy: over.missingStrategy ?? missingStrategy, numeric: over.missingNumeric ?? missingNumeric },
    };
    if (fam === "anomaly") {
      cfg.contamination = over.contamination ?? contamination;
    } else if (fam === "clustering") {
      const mode = over.kMode ?? kMode;
      if (mode === "fixed") cfg.n_clusters = over.nClusters ?? nClusters;
      else cfg.k_range = over.kRange ?? kRange;
    }
    return cfg;
  }, [family, algorithm, target, features, holdout, hpoEnabled, missingStrategy, missingNumeric, contamination, kMode, nClusters, kRange]);

  // ADR-0116 — confirm the refined config: writes the concrete columns, which drops
  // the provisional flag + intent fields (buildConfig produces a clean TrainConfig).
  const onConfirmReview = useCallback(() => {
    writeConfig(buildConfig({}));
  }, [writeConfig, buildConfig]);

  const fetchUpstreamColumns = useCallback(async () => {
    setColumnsLoading(true);
    setColumnsError(null);
    try {
      const data = await apiFetch<{ columns?: Array<{ name?: string }> }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(selectedNode.id)}/upstream-columns`,
      );
      setUpstreamColumns((data.columns ?? []).map((c) => c.name).filter((n): n is string => typeof n === "string" && n.trim().length > 0));
    } catch (e) {
      setColumnsError(e instanceof Error ? e.message : String(e));
    } finally {
      setColumnsLoading(false);
    }
  }, [operationId, selectedNode.id]);

  useEffect(() => { void fetchUpstreamColumns(); }, [fetchUpstreamColumns]);

  const loadNodeRuns = useCallback(async (mid: string, trainingId: string) => {
    try {
      const data = await apiFetch<{ node_runs: NodeRun[]; stage_timeline: StageTiming[] }>(
        `/api/tenant/models/${encodeURIComponent(mid)}/trainings/${encodeURIComponent(trainingId)}/node-runs`,
      );
      setNodeRuns(data.node_runs ?? []);
      setStageTimeline(data.stage_timeline ?? []);
    } catch {
      /* best-effort — the status header still renders without the timeline */
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const data = await apiFetch<ModelDetail>(`/api/tenant/models/${encodeURIComponent(id)}`);
      setDetail(data);
      const latestId = data.trainings?.[0]?.id;
      if (latestId) void loadNodeRuns(id, latestId);
      else setNodeRuns([]);
      return data;
    } catch {
      return null;
    }
  }, [loadNodeRuns]);

  useEffect(() => {
    if (modelId) void loadDetail(modelId);
    else setDetail(null);
  }, [modelId, loadDetail]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((id: string) => {
    stopPolling();
    pollRef.current = setInterval(() => {
      void (async () => {
        const data = await loadDetail(id);
        const latest = data?.trainings?.[0];
        if (!latest || !ACTIVE_STATUSES.has(latest.status)) stopPolling();
      })();
    }, 3000);
  }, [loadDetail, stopPolling]);

  // Resume polling if a training is already active when the node is opened.
  useEffect(() => {
    const latest = detail?.trainings?.[0];
    if (latest && ACTIVE_STATUSES.has(latest.status) && !pollRef.current && modelId) startPolling(modelId);
    return () => { /* keep polling across renders; cleared on unmount below */ };
  }, [detail, modelId, startPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const latestTraining = detail?.trainings?.[0] ?? null;
  const trainingActive = latestTraining ? ACTIVE_STATUSES.has(latestTraining.status) : false;

  // Tick once a second while a run is active so the elapsed timer advances.
  useEffect(() => {
    if (!trainingActive) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [trainingActive]);

  const champion = detail?.versions?.find((v) => v.is_current) ?? null;
  // The status reflects the MOST RECENTLY TRAINED version (highest version_number),
  // not the champion — a re-train lands as a non-promoted challenger, so showing
  // the champion would freeze the pill on v1's stats forever.
  const latestVersion = (detail?.versions ?? []).reduce<VersionRow | null>(
    (best, v) => (best === null || v.version_number > best.version_number ? v : best),
    null,
  );
  // Metric labels depend on the family the version was trained as, which is the
  // model's family — not the (possibly just-edited) form family.
  const trainedFamily = detail?.model.algorithm_family ?? family;

  // Training stage list (uniform per-step state, time, and detail).
  const tuningSummary = (latestTraining?.metrics as Record<string, unknown> | null)?.tuning as TuningSummary | undefined;
  const steps = latestTraining ? buildTrainingSteps(latestTraining, nodeRuns) : [];

  // ADR-0118 — missing-value handling. Gradient-boosted ingests NaN natively, so
  // "Automatic" leaves it alone there; every other algorithm gets numeric gaps filled.
  const gbSelected = algorithm === "gradient_boosted_regressor" || algorithm === "gradient_boosted_classifier";
  const autoImputes = missingStrategy === "auto" && !gbSelected;
  const showNumericFill = missingStrategy === "impute" || autoImputes;
  const numericFillWord = missingNumeric === "mean" ? "mean" : missingNumeric === "constant" ? "zero" : "median";

  function onChangeFamily(f: Family) {
    const algo = FAMILY_ALGOS[f][0]!.id;
    setFamily(f); setAlgorithm(algo);
    writeConfig(buildConfig({ family: f, algorithm: algo }));
  }
  function onChangeAlgorithm(a: string) { setAlgorithm(a); writeConfig(buildConfig({ algorithm: a })); }
  function onChangeTarget(t: string) {
    const nextFeatures = features.filter((f) => f.toLowerCase() !== t.toLowerCase());
    setTarget(t); setFeatures(nextFeatures);
    writeConfig(buildConfig({ target: t, features: nextFeatures }));
  }
  function toggleFeature(col: string) {
    // Case-insensitive so a column toggles cleanly even if a prior selection
    // was saved in a different case (matches the server's resolution).
    const has = features.some((f) => f.toLowerCase() === col.toLowerCase());
    const next = has ? features.filter((f) => f.toLowerCase() !== col.toLowerCase()) : [...features, col];
    setFeatures(next); writeConfig(buildConfig({ features: next }));
  }
  function removeStaleFeatures(stale: string[]) {
    const drop = new Set(stale);
    const next = features.filter((f) => !drop.has(f));
    setFeatures(next); writeConfig(buildConfig({ features: next }));
  }
  function onChangeHoldout(v: number) { setHoldout(v); writeConfig(buildConfig({ holdout: v })); }
  function onChangeHpo(v: boolean) { setHpoEnabled(v); writeConfig(buildConfig({ hpo: v })); }
  function onChangeMissingStrategy(s: MissingStrategy) { setMissingStrategy(s); writeConfig(buildConfig({ missingStrategy: s })); }
  function onChangeMissingNumeric(n: NumericFill) { setMissingNumeric(n); writeConfig(buildConfig({ missingNumeric: n })); }
  function onChangeContamination(v: number) { setContamination(v); writeConfig(buildConfig({ contamination: v })); }
  function onChangeKMode(m: "fixed" | "auto") { setKMode(m); writeConfig(buildConfig({ kMode: m })); }
  function onChangeNClusters(v: number) { setNClusters(v); writeConfig(buildConfig({ nClusters: v })); }
  function onChangeKRange(idx: 0 | 1, v: number) {
    const next: [number, number] = idx === 0 ? [v, kRange[1]] : [kRange[0], v];
    setKRange(next); writeConfig(buildConfig({ kRange: next }));
  }

  // Feature 0010: kill-switch for an in-flight (queued/running) training.
  async function onCancelTraining() {
    if (cancelBusy || !modelId || !latestTraining) return;
    setCancelBusy(true);
    try {
      await apiFetch(
        `/api/tenant/models/${encodeURIComponent(modelId)}/trainings/${encodeURIComponent(latestTraining.id)}/cancel`,
        { method: "POST" },
      );
      toast.push({ kind: "success", message: "Training cancelled" });
      await loadDetail(modelId);
    } catch (e) {
      toast.push({ kind: "error", message: `Couldn't cancel: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setCancelBusy(false);
    }
  }

  async function onTrain() {
    if (busy || trainingActive) return;
    setBusy(true);
    try {
      const flushed = await flushAndSave();
      if (!flushed) {
        toast.push({ kind: "error", message: "Couldn't save pending edits — fix the validation error above and try again." });
        return;
      }
      let id = modelId;
      if (!id) {
        const created = await apiFetch<{ model: { id: string; name: string } }>("/api/tenant/models", {
          method: "POST",
          body: { name: derivedModelName, source_operation_id: operationId, source_node_id: selectedNode.id },
        });
        id = created.model.id;
        setModelId(id);
        writeModelId(id);
      }
      await apiFetch<{ training: TrainingRow }>(`/api/tenant/models/${encodeURIComponent(id)}/train`, {
        method: "POST",
        body: { trigger_type: "manual" },
      });
      toast.push({ kind: "success", message: "Training started" });
      await loadDetail(id);
      startPolling(id);
    } catch (e) {
      toast.push({ kind: "error", message: `Training failed to start: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setBusy(false);
    }
  }

  // The model name is derived from the operation + this node's label (PDO-twin
  // convention; the hub re-derives it via syncModelLabelsForOperation on every
  // save). It is not independently editable — rename the node to rename the model.
  const derivedModelName = defaultModelName(operationName, selectedNode.data.label);

  const drift = detail?.spec_drift;
  const featureCandidates = upstreamColumns.filter((c) => c.toLowerCase() !== target.toLowerCase());
  // Ghost-selection detection: features/target saved in config that are no
  // longer in the upstream schema. Case-insensitive — a case-variant still
  // resolves at train time and is NOT stale. Only meaningful once the schema
  // has actually loaded (otherwise everything looks missing).
  const schemaReady = !columnsLoading && !columnsError && upstreamColumns.length > 0;
  const upstreamLower = new Set(upstreamColumns.map((c) => c.toLowerCase()));
  const staleFeatures = schemaReady
    ? features.filter((f) => f.toLowerCase() !== target.toLowerCase() && !upstreamLower.has(f.toLowerCase()))
    : [];
  const targetStale = schemaReady && !!target && !upstreamLower.has(target.toLowerCase());

  const isUnsupervised = UNSUPERVISED.has(family);
  const clusteringIncomplete =
    family === "clustering" &&
    (kMode === "fixed"
      ? !(Number.isInteger(nClusters) && nClusters >= 2)
      : !(kRange[0] >= 2 && kRange[1] >= kRange[0]));
  const trainDisabled =
    busy || trainingActive || isProvisional || features.length === 0 || staleFeatures.length > 0 ||
    (!isUnsupervised && (!target || targetStale)) || clusteringIncomplete;

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 600 }}>Model Train</div>

        {/* ADR-0116 — provisional (architect-created) node: review the AI-derived config before training. */}
        {isProvisional ? (
          <div className="card" style={{ borderColor: "var(--warn-border, var(--op-node-watch-border))", background: "var(--warn-bg, #fffbeb)", display: "grid", gap: 6 }}>
            {refine.status === "unresolved" ? (
              <>
                <div><span className="pill pillWarn">Needs review</span></div>
                <div className="help">Derived from your pipeline intent. Connect this node's input to your data, then reopen to finish setup.</div>
              </>
            ) : refine.status === "loading" ? (
              <div className="help">Matching columns from your pipeline intent…</div>
            ) : (
              <>
                <div><span className="pill pillWarn">Review</span> — derived from your pipeline intent</div>
                <div className="help">The target and inputs below were filled in from the data feeding this node. Check them, then confirm.</div>
                {refine.unmatched.length > 0 && !refineDismissed ? (
                  <div className="help">
                    Couldn't match: {refine.unmatched.join(", ")} — pick columns for these below.{" "}
                    <button className="btn btnSm" type="button" onClick={() => setRefineDismissed(true)}>Dismiss</button>
                  </div>
                ) : null}
                <div className="actions">
                  <button className="btn btnPrimary btnSm" type="button" onClick={onConfirmReview}>Confirm</button>
                </div>
              </>
            )}
          </div>
        ) : null}

        {/* Bound model / name. The name is derived from the operation + node
            label, so it is shown read-only here — rename the node to rename it. */}
        {modelId && detail ? (
          <div className="help">
            Model: <Link to={`/app/build/models/${encodeURIComponent(modelId)}`}>{detail.model.name}</Link>
          </div>
        ) : (
          <label className="field">
            <span className="label">Model name</span>
            <input className="input" value={derivedModelName} readOnly />
            <div className="help">Named from the operation and this node's label — rename the node to rename the model.</div>
          </label>
        )}

        <label className="field">
          <span className="label">Family</span>
          <select className="select" value={family} onChange={(e) => onChangeFamily(e.target.value as Family)}>
            <option value="classification">Classification</option>
            <option value="regression">Regression</option>
            <option value="anomaly">Anomaly detection</option>
            <option value="clustering">Clustering</option>
          </select>
        </label>

        <label className="field">
          <span className="label">Algorithm</span>
          <select className="select" value={algorithm} onChange={(e) => onChangeAlgorithm(e.target.value)}>
            {FAMILY_ALGOS[family].map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </label>

        {/* Column pickers */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span className="label">Columns</span>
          <button className="btn btnSm" type="button" onClick={() => void fetchUpstreamColumns()} disabled={columnsLoading}>
            {columnsLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
        {columnsError ? (
          <div className="help" style={{ color: "var(--danger-text)" }}>
            {columnsError} — run the upstream node once so its schema is available.
          </div>
        ) : null}

        {!isUnsupervised ? (
          <label className="field">
            <span className="label">Target column</span>
            <select className="select" value={target} onChange={(e) => onChangeTarget(e.target.value)}>
              <option value="">— select —</option>
              {upstreamColumns.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </label>
        ) : null}

        {family === "anomaly" ? (
          <label className="field">
            <span className="label">Contamination</span>
            <input
              className="input" type="number" min={0.01} max={0.49} step={0.01}
              value={contamination}
              onChange={(e) => onChangeContamination(Number(e.target.value))}
            />
            <span className="help">Expected share of rows that are outliers (0–0.5).</span>
          </label>
        ) : null}

        {family === "clustering" ? (
          <div className="field">
            <span className="label">Clusters</span>
            <select className="select" value={kMode} onChange={(e) => onChangeKMode(e.target.value as "fixed" | "auto")}>
              <option value="fixed">Fixed number</option>
              <option value="auto">Auto (best of a range)</option>
            </select>
            {kMode === "fixed" ? (
              <input
                className="input" type="number" min={2} step={1} style={{ marginTop: 6 }}
                value={nClusters}
                onChange={(e) => onChangeNClusters(Number(e.target.value))}
              />
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                <input className="input" type="number" min={2} step={1} value={kRange[0]} onChange={(e) => onChangeKRange(0, Number(e.target.value))} />
                <span className="help">to</span>
                <input className="input" type="number" min={2} step={1} value={kRange[1]} onChange={(e) => onChangeKRange(1, Number(e.target.value))} />
              </div>
            )}
            <span className="help">
              {kMode === "fixed" ? "Number of segments to find (at least 2)." : "Tries each k in the range and keeps the best silhouette score."}
            </span>
          </div>
        ) : null}

        <div className="field">
          <span className="label">Feature columns</span>
          {featureCandidates.length === 0 ? (
            <div className="help">No upstream columns yet. Connect and run the upstream node, then Refresh.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, minWidth: 0 }}>
              {featureCandidates.map((c) => (
                <label key={c} title={c} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <input type="checkbox" checked={features.some((f) => f.toLowerCase() === c.toLowerCase())} onChange={() => toggleFeature(c)} />
                  <span className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c}</span>
                </label>
              ))}
            </div>
          )}
          {targetStale ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>
              ⚠ The selected target “{target}” is no longer in the upstream data — pick a new target.
            </div>
          ) : null}
          {staleFeatures.length > 0 ? (
            <div className="help" style={{ color: "var(--danger-text)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>
                ⚠ {staleFeatures.length} selected feature{staleFeatures.length > 1 ? "s are" : " is"} no longer in the data:{" "}
                <span className="mono">{staleFeatures.join(", ")}</span>
              </span>
              <button className="btn btnSm" type="button" onClick={() => removeStaleFeatures(staleFeatures)}>
                Remove stale
              </button>
            </div>
          ) : null}
        </div>

        {!isUnsupervised ? (
          <label className="field">
            <span className="label">Holdout fraction</span>
            <input
              className="input" type="number" min={0.05} max={0.5} step={0.05}
              value={holdout}
              onChange={(e) => onChangeHoldout(Number(e.target.value))}
            />
            <span className="help">Share of rows held out to measure quality (0–1).</span>
          </label>
        ) : null}

        {!isUnsupervised ? (
          <label className="field">
            <span className="label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={hpoEnabled} onChange={(e) => onChangeHpo(e.target.checked)} />
              Tune settings automatically
            </span>
            <span className="help">
              Tries several internal settings and keeps the combination that scores best,
              measured by cross-validation on the training data. Training takes longer.
            </span>
          </label>
        ) : null}

        <label className="field">
          <span className="label">Missing values</span>
          <select className="select" value={missingStrategy} onChange={(e) => onChangeMissingStrategy(e.target.value as MissingStrategy)}>
            <option value="auto">Automatic (recommended)</option>
            <option value="impute">Fill values</option>
            <option value="drop">Drop rows</option>
            <option value="error">Fail if missing</option>
          </select>
          <span className="help">
            {missingStrategy === "auto"
              ? (gbSelected
                  ? "Gradient-boosted models can train on rows with blanks directly, so missing values are left as-is. Switch to Fill values, Drop rows, or Fail if missing to handle them yourself."
                  : `This model type can't train on blanks, so missing numbers are filled with the column ${numericFillWord} automatically. (Gradient-boosted models would keep them as-is.)`)
              : missingStrategy === "drop"
                ? "Rows with a missing numeric value are left out of training. Incomplete rows can't be scored later — choose Fill values if you need to score them."
                : missingStrategy === "error"
                  ? "Training stops if any numeric input is missing — use this to force clean upstream data."
                  : "Numeric gaps are filled before training; the fill is learned from the data and travels with the model."}
          </span>
        </label>

        {showNumericFill ? (
          <label className="field">
            <span className="label">Numeric fill</span>
            <select className="select" value={missingNumeric} onChange={(e) => onChangeMissingNumeric(e.target.value as NumericFill)}>
              <option value="median">Median</option>
              <option value="mean">Mean</option>
              <option value="constant">Zero</option>
            </select>
          </label>
        ) : null}

        <div className="actions">
          <button className="btn btnPrimary" type="button" onClick={() => void onTrain()} disabled={trainDisabled}>
            {modelId ? "Re-train" : "Create & train"}
          </button>
          {trainingActive && latestTraining ? (
            <button className="btn btnDanger" type="button" onClick={() => void onCancelTraining()} disabled={cancelBusy}>
              Cancel training
            </button>
          ) : null}
        </div>
      </div>

      {/* Drift banner */}
      {drift?.has_drift && drift.changes && drift.changes.length > 0 && !trainingActive ? (
        <div className="card" style={{ borderColor: "var(--warn-border, var(--op-node-watch-border))", background: "var(--warn-bg, #fffbeb)" }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Config changed since last train</div>
          <div className="help" style={{ marginBottom: 6 }}>Re-train to apply these changes:</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {drift.changes.map((c) => (
              <li key={c.field} className="help"><strong>{c.label}:</strong> {c.before} → {c.after}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Status — uniform stage list */}
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 600 }}>Status</div>
        {!latestTraining ? (
          <div><span className="pill">Not started</span></div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {/* Header: status pill (+ champion/challenger) · timing */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {latestTraining.status === "queued" ? (
                  <span className="pill pillWarn">queued</span>
                ) : latestTraining.status === "running" ? (
                  <span className="pill pillWarn">● running</span>
                ) : latestTraining.status === "failed" ? (
                  <span className="pill pillBad">● failed</span>
                ) : latestTraining.status === "cancelled" ? (
                  <span className="pill">● cancelled</span>
                ) : (
                  <span className="pill pillOk">● trained{latestVersion ? ` · v${latestVersion.version_number}` : ""}</span>
                )}
                {latestTraining.status === "success" && latestVersion ? (
                  latestVersion.is_current ? <span className="pill pillOk">champion</span> : <span className="pill">challenger</span>
                ) : null}
              </span>
              <span className="help" style={{ margin: 0 }}>
                {latestTraining.started_at ? `Started ${fmtTs(latestTraining.started_at)}` : `Queued ${fmtTs(latestTraining.created_at)}`}
                {trainingActive && latestTraining.started_at
                  ? ` · ${fmtDuration(nowTick - Date.parse(latestTraining.started_at))} elapsed`
                  : latestTraining.duration_ms ? ` · took ${fmtDuration(latestTraining.duration_ms)}` : ""}
              </span>
            </div>

            {/* Stage table — Step · State · Time · Detail */}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <tbody>
                {steps.map((s) => {
                  const badge =
                    s.state === "done" ? <span className="pill pillOk">done</span>
                    : s.state === "active" ? <span className="pill pillWarn">active</span>
                    : s.state === "failed" ? <span className="pill pillBad">failed</span>
                    : s.state === "cancelled" ? <span className="pill">cancelled</span>
                    : <span className="pill">pending</span>;

                  // Per-step time: live ticking on the active step, persisted/computed duration otherwise.
                  const timing = stageTimeline.find((e) => e.stage === s.key);
                  let timeText = "";
                  if (s.state === "active" && timing?.started_at) timeText = fmtDuration(nowTick - Date.parse(timing.started_at));
                  else if (timing?.duration_ms != null) timeText = fmtDuration(timing.duration_ms);

                  let detailNode: ReactNode = null;
                  if (s.key === "resolving_data" && nodeRuns.length > 0) {
                    detailNode = (
                      <div style={{ display: "grid", gap: 1 }}>
                        {nodeRuns.map((r, j) => (
                          <div key={r.node_id ?? j} className="muted" style={{ fontSize: 12 }}>
                            {r.label} · {r.from_cache ? "cached" : "ran"}
                            {r.duration_ms != null ? ` · ${fmtDuration(r.duration_ms)}` : ""}
                            {r.row_count != null ? ` · ${r.row_count.toLocaleString()} rows` : ""}
                          </div>
                        ))}
                      </div>
                    );
                  } else if (s.key === "training") {
                    const sum = s.state === "done" && latestVersion ? metricSummary(trainedFamily, latestVersion.metrics) : "";
                    if (s.state === "active") {
                      detailNode = <span className="muted" style={{ fontSize: 12 }}>{hpoEnabled ? "tuning settings, then training on all rows" : "fitting the model"}</span>;
                    } else if (tuningSummary?.ran === false && tuningSummary.reason) {
                      detailNode = <span style={{ fontSize: 12, color: "var(--danger-text)" }}>tuning skipped — {tuningSummary.reason}{sum ? ` · ${sum}` : ""}</span>;
                    } else if (tuningSummary && tuningSummary.n_candidates != null) {
                      const bits = [
                        "auto-tuned",
                        `${tuningSummary.n_candidates} settings`,
                        tuningSummary.cv_folds != null ? `${tuningSummary.cv_folds}-fold CV` : null,
                        tuningSummary.cv_score != null ? `best CV ${tuningSummary.cv_score.toFixed(3)}` : null,
                      ].filter(Boolean);
                      detailNode = <span className="muted" style={{ fontSize: 12 }}>{bits.join(" · ")}{sum ? ` · ${sum}` : ""}</span>;
                    } else if (sum) {
                      detailNode = <span className="muted" style={{ fontSize: 12 }}>{sum}</span>;
                    }
                  } else if (s.key === "finalizing" && s.state === "done" && latestVersion) {
                    detailNode = <span className="muted" style={{ fontSize: 12 }}>v{latestVersion.version_number}</span>;
                  }
                  return (
                    <tr key={s.key} style={{ borderTop: "0.5px solid var(--border, #e5e7eb)" }}>
                      <td style={{ padding: "9px 8px 9px 0", width: "28%", verticalAlign: "top", color: s.state === "pending" ? "var(--muted)" : "var(--text)" }}>{s.label}</td>
                      <td style={{ padding: "9px 8px", width: 76, verticalAlign: "top" }}>{badge}</td>
                      <td style={{ padding: "9px 8px", width: 72, verticalAlign: "top", textAlign: "right", fontVariantNumeric: "tabular-nums", color: s.state === "active" ? "var(--accent, #4f46e5)" : "var(--muted)" }}>{timeText}</td>
                      <td style={{ padding: "9px 0 9px 8px", verticalAlign: "top" }}>{detailNode}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Failure detail */}
            {latestTraining.status === "failed" && latestTraining.error_message ? (
              <div className="help" style={{ color: "var(--danger-text)", margin: 0 }}>{latestTraining.error_message}</div>
            ) : null}

            {/* Promote hint for a successful challenger */}
            {latestTraining.status === "success" && latestVersion && !latestVersion.is_current && champion && modelId ? (
              <div className="help" style={{ margin: 0 }}>
                Champion is v{champion.version_number} ({metricSummary(trainedFamily, champion.metrics)}). Promote on the{" "}
                <Link to={`/app/build/models/${encodeURIComponent(modelId)}`}>model page</Link>.
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Recent trainings — compact; full history + version management on the model page */}
      {detail && detail.trainings.length > 0 ? (
        <div className="card" style={{ display: "grid", gap: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 600 }}>Recent trainings</div>
            {modelId ? <Link className="help" to={`/app/build/models/${encodeURIComponent(modelId)}`}>View all</Link> : null}
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            {detail.trainings.slice(0, 5).map((t) => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minWidth: 0 }}>
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.status === "success" ? <span className="pill pillOk">success</span>
                    : t.status === "failed" ? <span className="pill pillBad">failed</span>
                    : <span className="pill pillWarn">{t.status}</span>}
                  {t.status === "success" && t.metrics ? <span className="help" style={{ marginLeft: 6 }}>{metricSummary(trainedFamily, t.metrics)}</span> : null}
                </span>
                <span className="help" style={{ whiteSpace: "nowrap" }}>{fmtTs(t.completed_at ?? t.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
